import { useEffect } from "react";
import { Account, TransactionCustomFieldValues } from "../../../types";
import { TransactionWithDate } from "../../../utils/transactionProcessing";
import { formatRate } from "../../../utils/transactionUtils";

interface FormData {
  title: string;
  category: string;
  amount: string;
  currency: string;
  rate: string;
  defaultCurrencyRate: string;
  date: string;
  time: string;
  accountId: string;
  accountGroup: string;
  details?: any;
  customFieldValues?: TransactionCustomFieldValues;
  customFieldPendingFiles?: Record<string, File>;
}

interface TransferFormData {
  fromAccountId: string;
  toAccountId: string;
  amount: string;
  date: string;
  time: string;
  transferRate: string;
  customFieldValues?: TransactionCustomFieldValues;
  customFieldPendingFiles?: Record<string, File>;
  feeAmount?: string;
  feeAccountId?: string;
}

interface UseRateAutoUpdateProps {
  formData: FormData;
  transferFormData: TransferFormData;
  accounts: Account[];
  defaultCurrency: string;
  editingTransaction: TransactionWithDate | null;
  initialFormData: FormData | null;
  initialTransferFormData: TransferFormData | null;
  rateFromTransaction: boolean;
  rateManuallyEdited: boolean;
  defaultCurrencyRateManuallyEdited: boolean;
  calculateTransferRate: (fromCurrency: string, toCurrency: string) => number;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  setTransferFormData: React.Dispatch<React.SetStateAction<TransferFormData>>;
  setInitialFormData: React.Dispatch<React.SetStateAction<FormData | null>>;
  setInitialTransferFormData: React.Dispatch<React.SetStateAction<TransferFormData | null>>;
  setRateFromTransaction: (value: boolean) => void;
  setRateManuallyEdited: (value: boolean) => void;
  setDefaultCurrencyRateManuallyEdited: (value: boolean) => void;
}

/**
 * Hook to auto-update exchange rates when accounts or currencies change.
 * Handles both expense/income and transfer transactions.
 */
export const useRateAutoUpdate = ({
  formData,
  transferFormData,
  accounts,
  defaultCurrency,
  editingTransaction,
  initialFormData,
  initialTransferFormData,
  rateFromTransaction,
  rateManuallyEdited,
  defaultCurrencyRateManuallyEdited,
  calculateTransferRate,
  setFormData,
  setTransferFormData,
  setInitialFormData,
  setInitialTransferFormData,
  setRateFromTransaction,
  setRateManuallyEdited,
  setDefaultCurrencyRateManuallyEdited,
}: UseRateAutoUpdateProps): void => {
  // Update transfer rate when accounts change
  useEffect(() => {
    if (transferFormData.fromAccountId && transferFormData.toAccountId) {
      const fromAccount = accounts.find((acc) => acc.id === transferFormData.fromAccountId);
      const toAccount = accounts.find((acc) => acc.id === transferFormData.toAccountId);
      
      if (fromAccount && toAccount && fromAccount.currency !== toAccount.currency) {
        const calculatedRate = calculateTransferRate(fromAccount.currency, toAccount.currency);
        const storedRate = transferFormData.transferRate ? parseFloat(transferFormData.transferRate) : null;
        
        // Check if stored rate is valid and reasonable
        const isValidStoredRate = storedRate !== null && !isNaN(storedRate) && storedRate > 0 && storedRate >= 0.0001 && storedRate <= 10000;
        
        // If rate is empty, invalid, or unreasonable, auto-populate
        if (!transferFormData.transferRate || transferFormData.transferRate === "" || !isValidStoredRate) {
          setTransferFormData((prev) => ({
            ...prev,
            transferRate: formatRate(calculatedRate),
          }));
        } else if (isValidStoredRate) {
          // Check if stored rate is significantly different from calculated rate (>10% difference)
          const rateDifference = Math.abs(storedRate - calculatedRate) / Math.max(storedRate, calculatedRate);
          if (rateDifference > 0.1) {
            // Rate is significantly different, auto-correct
            setTransferFormData((prev) => ({
              ...prev,
              transferRate: formatRate(calculatedRate),
            }));
          }
        }
      } else if (fromAccount && toAccount && fromAccount.currency === toAccount.currency) {
        // Same currency, clear rate field
        setTransferFormData((prev) => ({
          ...prev,
          transferRate: "",
        }));
      }
    }
  }, [transferFormData.fromAccountId, transferFormData.toAccountId, transferFormData.transferRate, accounts, defaultCurrency, calculateTransferRate, setTransferFormData]);

  // Normalize amount/rate for "just initialized" comparison (avoid "100" !== "100.0")
  const normalizeNum = (s: string | undefined): string => {
    if (!s || s.trim() === "") return "";
    const n = parseFloat(s);
    return isNaN(n) ? (s || "").trim() : n.toString();
  };

  // Auto-update rate when account or currency changes for expense/income
  useEffect(() => {
    // Skip if we're editing and formData matches initialFormData (just initialized, don't modify yet)
    if (editingTransaction && editingTransaction.type !== "transfer" && initialFormData) {
      const currentAmount = normalizeNum(formData.amount) || "0";
      const initialAmount = normalizeNum(initialFormData.amount) || "0";
      const rateSame = normalizeNum(formData.rate) === normalizeNum(initialFormData.rate);
      const defaultRateSame = normalizeNum(formData.defaultCurrencyRate) === normalizeNum(initialFormData.defaultCurrencyRate);
      const isInitialized =
        (formData.title || "").trim() === (initialFormData.title || "").trim() &&
        (formData.category || "").trim() === (initialFormData.category || "").trim() &&
        currentAmount === initialAmount &&
        formData.date === initialFormData.date &&
        formData.time === initialFormData.time &&
        formData.accountId === initialFormData.accountId &&
        formData.currency === initialFormData.currency &&
        rateSame &&
        defaultRateSame;
      
      // If formData matches initialFormData (just initialized), skip auto-update so Update stays disabled
      if (isInitialized) {
        return;
      }
    }
    
    if (formData.accountId && formData.currency) {
      const account = accounts.find((acc) => acc.id === formData.accountId);
      if (account) {
        const accountCurrency = account.currency || defaultCurrency;
        if (formData.currency !== accountCurrency) {
          const calculatedRate = calculateTransferRate(formData.currency, accountCurrency);
          const storedRate = formData.rate ? parseFloat(formData.rate) : null;
          
          // Check if stored rate is valid and reasonable
          const isValidStoredRate = storedRate !== null && !isNaN(storedRate) && storedRate > 0 && storedRate >= 0.0001 && storedRate <= 10000;
          
          // If rate is empty, invalid, or unreasonable, auto-populate (unless manually edited or from transaction)
          if (!formData.rate || formData.rate === "" || !isValidStoredRate) {
            if (!rateFromTransaction && !rateManuallyEdited) {
              setFormData((prev) => ({
                ...prev,
                rate: formatRate(calculatedRate),
              }));
            }
          } else if (isValidStoredRate && !rateFromTransaction && !rateManuallyEdited && !editingTransaction) {
            // For new transactions, if rate differs significantly from calculated rate (>1% difference), auto-correct
            // This handles cases where stale data might be present
            const rateDifference = Math.abs(storedRate - calculatedRate) / Math.max(storedRate, calculatedRate);
            if (rateDifference > 0.01) {
              setFormData((prev) => ({
                ...prev,
                rate: formatRate(calculatedRate),
              }));
            }
          }
          // Don't auto-correct rates that come from transactions or were manually edited - preserve them as-is
          // User can use refresh button if they want to update to current rates
        } else {
          // Same currency, clear rate field
          setFormData((prev) => ({
            ...prev,
            rate: "",
          }));
          // Reset flags when currencies match
          setRateFromTransaction(false);
          setRateManuallyEdited(false);
        }
      }
    }
    
    // Auto-update default currency rate when currency changes (independent of account selection)
    if (formData.currency && formData.currency !== defaultCurrency) {
      // When editing, if transaction has a stored defaultCurrencyRate, ALWAYS preserve it (never auto-update)
      if (editingTransaction && editingTransaction.type !== "transfer") {
        // Check if transaction has a stored defaultCurrencyRate
        const transactionHasStoredRate = editingTransaction.defaultCurrencyRate !== undefined && editingTransaction.defaultCurrencyRate !== null;
        // Check if initialFormData has the stored rate (was loaded from transaction)
        const initialHasStoredRate = initialFormData && initialFormData.defaultCurrencyRate && initialFormData.defaultCurrencyRate !== "";
        // If transaction has stored rate OR initialFormData has stored rate, preserve it
        if (transactionHasStoredRate || initialHasStoredRate) {
          // If formData already has the stored rate, don't modify it
          if (formData.defaultCurrencyRate && formData.defaultCurrencyRate !== "") {
            // Preserve the stored rate - don't auto-update
            return;
          }
        }
      }

      // Skip auto-update if editing and formData matches initialFormData (preserve stored rate from transaction)
      if (editingTransaction && editingTransaction.type !== "transfer" && initialFormData) {
        const currentAmountNorm = normalizeNum(formData.amount) || "0";
        const initialAmountNorm = normalizeNum(initialFormData.amount) || "0";
        const defaultRateSame = normalizeNum(formData.defaultCurrencyRate) === normalizeNum(initialFormData.defaultCurrencyRate);
        const isInitialized =
          (formData.title || "").trim() === (initialFormData.title || "").trim() &&
          (formData.category || "").trim() === (initialFormData.category || "").trim() &&
          currentAmountNorm === initialAmountNorm &&
          formData.date === initialFormData.date &&
          formData.time === initialFormData.time &&
          formData.accountId === initialFormData.accountId &&
          formData.currency === initialFormData.currency &&
          defaultRateSame;

        // If formData matches initialFormData (just initialized with stored rate), skip auto-update
        if (isInitialized) {
          // Don't modify default currency rate - preserve the stored rate from transaction
          return;
        }
      }

      // Calculate rate from account currency to default currency (not expense currency)
      const account = formData.accountId ? accounts.find((acc) => acc.id === formData.accountId) : null;
      const accountCurrency = account?.currency || defaultCurrency;
      const calculatedAccountToDefaultRate = accountCurrency !== defaultCurrency 
        ? calculateTransferRate(accountCurrency, defaultCurrency)
        : 1;
      const storedDefaultRate = formData.defaultCurrencyRate ? parseFloat(formData.defaultCurrencyRate) : null;

      // Check if stored default rate is valid and reasonable
      const isValidStoredDefaultRate = storedDefaultRate !== null && !isNaN(storedDefaultRate) && storedDefaultRate > 0 && storedDefaultRate >= 0.0001 && storedDefaultRate <= 10000;

      // If default rate is empty, invalid, or unreasonable, auto-populate (unless manually edited)
      // IMPORTANT: If rate already exists and is valid, preserve it (don't auto-update when editing)
      if (!formData.defaultCurrencyRate || formData.defaultCurrencyRate === "" || !isValidStoredDefaultRate) {
        // When editing, don't auto-fill if transaction has a stored rate (even if formData is empty)
        const shouldAutoFill = !(editingTransaction && editingTransaction.type !== "transfer" &&
          (editingTransaction.defaultCurrencyRate !== undefined || (initialFormData && initialFormData.defaultCurrencyRate)));

        if (!defaultCurrencyRateManuallyEdited && shouldAutoFill && accountCurrency !== defaultCurrency) {
          const formattedRate = formatRate(calculatedAccountToDefaultRate);
          setFormData((prev) => ({
            ...prev,
            defaultCurrencyRate: formattedRate,
          }));
        }
      }
      // Don't auto-correct default currency rates that exist - preserve them as-is (from transaction or manual edit)
      // User can use refresh button if they want to update to current rates
    } else if (formData.currency === defaultCurrency) {
      // Same currency as default, clear default currency rate field
      if (formData.defaultCurrencyRate) {
        setFormData((prev) => ({
          ...prev,
          defaultCurrencyRate: "",
        }));
        setDefaultCurrencyRateManuallyEdited(false);
      }
    }
  }, [formData.accountId, formData.currency, formData.rate, formData.defaultCurrencyRate, formData.title, formData.category, formData.amount, formData.date, formData.time, accounts, defaultCurrency, rateFromTransaction, rateManuallyEdited, defaultCurrencyRateManuallyEdited, editingTransaction, initialFormData, calculateTransferRate, setFormData, setRateFromTransaction, setRateManuallyEdited, setDefaultCurrencyRateManuallyEdited]);
  
  // Don't sync rate into initialFormData when editing so "Update rate" → Update button stays enabled.
  // Sync initialTransferFormData with transferFormData after rate auto-update when editing (to keep them in sync)
  useEffect(() => {
    // Only run when editing (not when creating new transactions)
    if (!editingTransaction || editingTransaction.type !== "transfer" || !initialTransferFormData) {
      return;
    }
    
    // Check if this is still the initial state (user hasn't made changes yet)
    const currentAmount = transferFormData.amount === "" ? "0" : transferFormData.amount;
    const initialAmount = initialTransferFormData.amount === "" ? "0" : initialTransferFormData.amount;
    const isStillInitial = 
      transferFormData.fromAccountId === initialTransferFormData.fromAccountId &&
      transferFormData.toAccountId === initialTransferFormData.toAccountId &&
      currentAmount === initialAmount &&
      transferFormData.date === initialTransferFormData.date &&
      transferFormData.time === initialTransferFormData.time;
    
    // If still in initial state but transferRate changed (due to useEffect), sync initialTransferFormData.transferRate
    if (isStillInitial && transferFormData.transferRate !== initialTransferFormData.transferRate) {
      setInitialTransferFormData((prev) => prev ? { ...prev, transferRate: transferFormData.transferRate } : prev);
    }
  }, [transferFormData.transferRate, transferFormData.fromAccountId, transferFormData.toAccountId, transferFormData.amount, transferFormData.date, transferFormData.time, editingTransaction, initialTransferFormData, setInitialTransferFormData]);
};
